import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { requestStatsService } from '../../services/admin/requestStats';
import type {
  TimelinePeriod,
  TimelineGroupBy,
  TimelinePoint,
  TopCampaign,
  SubscriptionUsageItem,
} from '../../services/admin/requestStats';

const PERIOD_OPTIONS: { value: TimelinePeriod; label: string; defaultGroup: TimelineGroupBy }[] = [
  { value: '24h', label: 'Last 24h', defaultGroup: 'hour' },
  { value: '7d',  label: 'Last 7 days', defaultGroup: 'day' },
  { value: '30d', label: 'Last 30 days', defaultGroup: 'day' },
  { value: '90d', label: 'Last 90 days', defaultGroup: 'day' },
  { value: '1y',  label: 'Last year', defaultGroup: 'month' },
];

const GROUP_OPTIONS: { value: TimelineGroupBy; label: string }[] = [
  { value: 'hour',  label: 'Hourly' },
  { value: 'day',   label: 'Daily' },
  { value: 'week',  label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];

function formatNumber(n: number): string {
  return n.toLocaleString();
}

function formatPct(n: number | null): string {
  if (n === null) return '∞';
  return `${n}%`;
}

function usageColor(pct: number | null): string {
  if (pct === null) return 'text-gray-400';
  if (pct >= 90) return 'text-red-600';
  if (pct >= 70) return 'text-orange-500';
  return 'text-green-600';
}

function barWidth(pct: number | null): string {
  if (pct === null) return '0%';
  return `${Math.min(100, pct)}%`;
}

function barColor(pct: number | null): string {
  if (pct === null) return 'bg-gray-200';
  if (pct >= 90) return 'bg-red-500';
  if (pct >= 70) return 'bg-orange-400';
  return 'bg-green-500';
}

type Tab = 'timeline' | 'campaigns' | 'subscriptions';

export const AdminRequestStatsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('timeline');

  // Timeline state
  const [period, setPeriod] = useState<TimelinePeriod>('30d');
  const [groupBy, setGroupBy] = useState<TimelineGroupBy>('day');
  const [timelineData, setTimelineData] = useState<TimelinePoint[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Campaigns state
  const [campaignPeriod, setCampaignPeriod] = useState<string>('30d');
  const [campaigns, setCampaigns] = useState<TopCampaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  // Subscription state
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionUsageItem[]>([]);
  const [subscriptionTotal, setSubscriptionTotal] = useState({ used: 0, limit: 0 });
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const loadTimeline = useCallback(async () => {
    setTimelineLoading(true);
    setError(null);
    try {
      const res = await requestStatsService.getTimeline(period, groupBy);
      setTimelineData(res.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load timeline');
    } finally {
      setTimelineLoading(false);
    }
  }, [period, groupBy]);

  const loadCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    setError(null);
    try {
      const res = await requestStatsService.getTopCampaigns(campaignPeriod);
      setCampaigns(res.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load campaigns');
    } finally {
      setCampaignsLoading(false);
    }
  }, [campaignPeriod]);

  const loadSubscriptions = useCallback(async () => {
    setSubscriptionLoading(true);
    setError(null);
    try {
      const res = await requestStatsService.getSubscriptionUsage();
      setSubscriptionData(res.data);
      setSubscriptionTotal({ used: res.total_used, limit: res.total_limit });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load subscription usage');
    } finally {
      setSubscriptionLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'timeline') loadTimeline();
  }, [activeTab, loadTimeline]);

  useEffect(() => {
    if (activeTab === 'campaigns') loadCampaigns();
  }, [activeTab, loadCampaigns]);

  useEffect(() => {
    if (activeTab === 'subscriptions') loadSubscriptions();
  }, [activeTab, loadSubscriptions]);

  const handlePeriodChange = (p: TimelinePeriod) => {
    const opt = PERIOD_OPTIONS.find(o => o.value === p);
    setPeriod(p);
    if (opt) setGroupBy(opt.defaultGroup);
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: 'timeline',      label: 'Request Timeline' },
    { key: 'campaigns',     label: 'Top Campaigns' },
    { key: 'subscriptions', label: 'Subscription Usage' },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Request Statistics</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Traffic overview, top campaigns, and subscription usage
          </p>
        </div>
        <button
          onClick={() => {
            if (activeTab === 'timeline') loadTimeline();
            else if (activeTab === 'campaigns') loadCampaigns();
            else loadSubscriptions();
          }}
          className="btn btn-secondary text-sm flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-3 underline">Dismiss</button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── TIMELINE TAB ── */}
      {activeTab === 'timeline' && (
        <div className="space-y-4">
          {/* Controls */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {PERIOD_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => handlePeriodChange(opt.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    period === opt.value
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {GROUP_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setGroupBy(opt.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    groupBy === opt.value
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Chart */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            {timelineLoading ? (
              <div className="flex items-center justify-center h-72">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
              </div>
            ) : timelineData.length === 0 ? (
              <div className="flex items-center justify-center h-72 text-gray-400 text-sm">
                No data for selected period
              </div>
            ) : (
              <>
                {/* Summary stats above chart */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumber(timelineData.reduce((s, d) => s + d.total, 0))}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">Total Requests</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {formatNumber(timelineData.reduce((s, d) => s + d.humans, 0))}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">Humans</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-500">
                      {formatNumber(timelineData.reduce((s, d) => s + d.bots, 0))}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">Bots</p>
                  </div>
                </div>

                <ResponsiveContainer width="100%" height={320}>
                  <RechartsLineChart data={timelineData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="period"
                      tick={{ fontSize: 11, fill: '#6b7280' }}
                      tickFormatter={v => {
                        const d = new Date(v);
                        if (groupBy === 'hour') return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        if (groupBy === 'month') return d.toLocaleDateString([], { month: 'short', year: '2-digit' });
                        return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
                      }}
                    />
                    <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                      formatter={(value: number, name: string) => [formatNumber(value), name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="total"  stroke="#6366f1" strokeWidth={2} dot={false} name="Total" />
                    <Line type="monotone" dataKey="humans" stroke="#10b981" strokeWidth={2} dot={false} name="Humans" />
                    <Line type="monotone" dataKey="bots"   stroke="#ef4444" strokeWidth={2} dot={false} name="Bots" />
                  </RechartsLineChart>
                </ResponsiveContainer>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── TOP CAMPAIGNS TAB ── */}
      {activeTab === 'campaigns' && (
        <div className="space-y-4">
          {/* Period selector */}
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
            {(['24h', '7d', '30d', '90d'] as const).map(p => (
              <button
                key={p}
                onClick={() => setCampaignPeriod(p)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  campaignPeriod === p
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {p === '24h' ? 'Last 24h' : p === '7d' ? 'Last 7d' : p === '30d' ? 'Last 30d' : 'Last 90d'}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {campaignsLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-10">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Campaign</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Account</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Total</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Humans</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Bots</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Bot %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {campaigns.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                        No campaign data for this period
                      </td>
                    </tr>
                  ) : (
                    campaigns.map((c, i) => (
                      <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-400 font-medium">{i + 1}</td>
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">{c.name}</span>
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/admin/accounts?search=${encodeURIComponent(c.account_name)}`}
                            className="text-indigo-600 hover:text-indigo-800 hover:underline"
                          >
                            {c.account_name}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            c.status === 'active'
                              ? 'bg-green-100 text-green-700'
                              : c.status === 'paused'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">
                          {formatNumber(c.requests_count)}
                        </td>
                        <td className="px-4 py-3 text-right text-green-700">
                          {formatNumber(c.human_count)}
                        </td>
                        <td className="px-4 py-3 text-right text-red-600">
                          {formatNumber(c.bot_count)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-medium ${c.bot_percentage >= 70 ? 'text-red-600' : c.bot_percentage >= 40 ? 'text-orange-500' : 'text-gray-700'}`}>
                            {c.bot_percentage}%
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── SUBSCRIPTION USAGE TAB ── */}
      {activeTab === 'subscriptions' && (
        <div className="space-y-4">
          {/* Summary cards */}
          {!subscriptionLoading && subscriptionData.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Accounts w/ Usage</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{subscriptionData.length}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Total Requests This Month</p>
                <p className="text-3xl font-bold text-indigo-600 mt-1">{formatNumber(subscriptionTotal.used)}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Combined Plan Capacity</p>
                <p className="text-3xl font-bold text-gray-700 mt-1">{formatNumber(subscriptionTotal.limit)}</p>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {subscriptionLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Account</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Plan</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Used</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Limit</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Remaining</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[140px]">Usage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {subscriptionData.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                        No subscription usage data for this month
                      </td>
                    </tr>
                  ) : (
                    subscriptionData.map(item => (
                      <tr key={item.account_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/admin/accounts?search=${encodeURIComponent(item.account_name)}`}
                              className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
                            >
                              {item.account_name}
                            </Link>
                            {item.account_status !== 'active' && (
                              <span className="px-1.5 py-0.5 rounded text-xs bg-yellow-100 text-yellow-700">
                                {item.account_status}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{item.plan_name}</td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">
                          {formatNumber(item.requests_used)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-500">
                          {item.requests_limit !== null ? formatNumber(item.requests_limit) : '∞'}
                        </td>
                        <td className={`px-4 py-3 text-right font-medium ${usageColor(item.usage_pct)}`}>
                          {item.requests_remain !== null ? formatNumber(item.requests_remain) : '∞'}
                        </td>
                        <td className="px-4 py-3">
                          {item.usage_pct !== null ? (
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                                <div
                                  className={`h-2 rounded-full transition-all ${barColor(item.usage_pct)}`}
                                  style={{ width: barWidth(item.usage_pct) }}
                                />
                              </div>
                              <span className={`text-xs font-medium w-10 text-right ${usageColor(item.usage_pct)}`}>
                                {formatPct(item.usage_pct)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">Unlimited</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

AdminRequestStatsPage.displayName = 'AdminRequestStatsPage';

export default AdminRequestStatsPage;
